import { msg } from "@lingui/core/macro";
import { type Accessor, createMemo, Show } from "solid-js";
import { Trash2 } from "@fleet/icons";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { getHasLocalDataQuery } from "@fieldcheck/powersync";
import { mapHasLocalData, queryKeys } from "@fieldcheck/shared";
import { createPowerSyncQuery } from "@fieldcheck/shared/solid";

import { Button } from "@/components/ui/button";
import { useSignOut } from "@/providers/boot-manager";
import { useI18n } from "@/providers/i18n-provider";

interface ClearLocalDataButtonProps {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  userId: Accessor<string | null>;
}

export function ClearLocalDataButton(props: ClearLocalDataButtonProps) {
  const { t } = useI18n();
  const { promptClearData, isSigningOut } = useSignOut();

  const hasLocalData = createPowerSyncQuery<{ hasData: number }>({
    queryKey: () => queryKeys.powersync.hasLocalData(props.userId() ?? undefined),
    db: () => props.db(),
    staleTime: 0,
    gcTime: 0,
    queryFn: () => {
      const uid = props.userId();
      if (!uid) {
        return null;
      }
      const q = getHasLocalDataQuery(uid);
      return {
        execute: async () => {
          const db = props.db();
          if (!db) {
            return [];
          }
          return db.getAll<{ hasData: number }>(q.sql, q.parameters);
        },
        compile: () => q,
      };
    },
    enabled: () => Boolean(props.userId()),
  });

  const hasData = createMemo(() => mapHasLocalData(hasLocalData.data));

  return (
    <Show when={hasData()}>
      <Button
        variant="destructive"
        class="w-full rounded-2xl gap-2 h-12"
        onClick={() => {
          promptClearData();
        }}
        disabled={isSigningOut()}
      >
        <Trash2 class="h-5 w-5" />
        {t(msg`Clear local data`)}
      </Button>
    </Show>
  );
}
